import { Building2 } from "lucide-react";
import {
  PageHeading, Section, Steps, Callout,
  Diagram, MockTabs, MockTable,
} from "../shared";

export default function PagesOriginsSection() {
  return (
    <div data-testid="guide-pages-origins">
      <PageHeading
        icon={Building2}
        title="Origins"
        description="A page that sorts every action item by where it came from — for example which board meeting, audit, or directive created it. It's the easiest way to see all the work that came out of one source in a single view."
      />

      <Callout variant="info" title="In plain words">
        Imagine all your action items in a filing cabinet. The Origins page is the <span className="text-white">drawer labelled "where did this come from?"</span> — it groups items by their origin type (like Board Resolution), then by the exact meeting or document inside that type.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li><span className="text-white">Audit prep</span> — pull every item from one specific meeting in one view.</li>
          <li><span className="text-white">Reporting</span> — show progress across all items from a particular committee.</li>
          <li><span className="text-white">Tracing</span> — follow an item from its source document all the way through to closure.</li>
        </ul>
      </Section>

      <Section title="A picture of the page">
        <Diagram
          frameLabel="Origins"
          legend={[
            <>The <span className="text-white">Active</span> tab shows items that are still being worked on (not yet finished).</>,
            <>The <span className="text-white">Completed</span> tab shows finished items — Closed, Sent Upward, or Cancelled.</>,
            <>Items are grouped by <span className="text-white">Origin Type</span> (Board Resolution, Chairman Directive, IC Decision, and so on), then sub-grouped by the exact <span className="text-white">meeting or reference</span>, so everything from one meeting sits together.</>,
          ]}
        >
          <MockTabs
            tabs={[
              { label: "Active", active: true, marker: 1 },
              { label: "Completed", marker: 2 },
            ]}
          />
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
            <p className="text-xs text-white/70 mb-1 flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5 text-cyan-300" />
              <span className="text-white">Board Resolution</span>
              <span className="text-2xs text-white/40">→ March Board Meeting</span>
            </p>
            <MockTable
              columns={["Item", "Owner", "Status", "Lane", "Due"]}
              rows={[
                ["Approve Q1 budget", "Sara K.", "In Progress", "SDF MD", "30 May"],
                ["Update risk register", "Ravi P.", "Registered", "EDGE MD", "12 Jun"],
              ]}
            />
          </div>
        </Diagram>
        <Steps
          steps={[
            <>Open the <span className="text-white">Active</span> tab to see live work, grouped by where it came from.</>,
            <>Switch to <span className="text-white">Completed</span> when you need the finished items instead.</>,
            <>Scroll to the <span className="text-white">Origin Type</span> and the specific <span className="text-white">meeting / reference</span> you care about to see all its items together.</>,
          ]}
        />
      </Section>

      <Callout variant="info">
        Origin types are set up by a Super Admin under <span className="text-white">Admin → Reference Lists</span>. New types can be added there as your governance evolves.
      </Callout>
    </div>
  );
}
